import React, { useState, useMemo, useEffect } from 'react';
import { Box, Card, Typography, ButtonGroup, Button, Select, MenuItem, OutlinedInput, Checkbox, ListItemText, Badge } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend, BarChart, Bar, AreaChart, Area } from 'recharts';

const COLORS = ['#90caf9', '#F9AA33', '#82ca9d', '#ff7300', '#8884d8', '#ff9800', '#4caf50', '#d32f2f'];

// Helper to get month label
const getMonthLabel = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const YieldTrendChart = ({ cropsData, harvestsData, zones = [] }) => {
  const [view, setView] = useState('total'); // 'total' | 'section' | 'cropType'
  const [selectedCropTypes, setSelectedCropTypes] = useState([]);

  // Debug logging with more detail
  console.log('YieldTrendChart received:', {
    cropsDataLength: cropsData?.length,
    harvestsDataLength: harvestsData?.length,
    zonesLength: zones?.length,
    cropsData: cropsData?.slice(0, 2), // First 2 items
    harvestsData: harvestsData?.slice(0, 2), // First 2 items
    zones: zones?.slice(0, 2), // First 2 items
    cropsDataIsArray: Array.isArray(cropsData),
    harvestsDataIsArray: Array.isArray(harvestsData),
    zonesIsArray: Array.isArray(zones),
    timestamp: new Date().toISOString()
  });

  // Get all section names, filtering out 'Unknown' or missing names
  const allSections = useMemo(() => {
    return Array.from(new Set(
      cropsData.map(crop => {
        const zone = zones?.find(z => z.id === crop.zone_id);
        return zone?.name || null;
      }).filter(Boolean)
    ));
  }, [cropsData, zones]);

  // Get all crop types (normalize for grouping similar spellings)
  const normalize = s => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const allCropTypes = useMemo(() => {
    const typeMap = {};
    cropsData.forEach(crop => {
      const raw = crop.plant_type_cl || crop.plant_type || 'Unknown';
      const norm = normalize(raw);
      if (!typeMap[norm]) typeMap[norm] = raw;
    });
    return Object.values(typeMap);
  }, [cropsData]);

  // Total: Sum of all harvest_amt in farmData.harvests, grouped by month
  const totalTrend = useMemo(() => {
    const byMonth = {};
    harvestsData.forEach(harvest => {
      const month = getMonthLabel(harvest.date);
      if (!byMonth[month]) byMonth[month] = 0;
      byMonth[month] += harvest.harvest_amt || 0;
    });
    return Object.keys(byMonth).sort().map(month => ({ month, yield: Number(byMonth[month].toFixed(2)) }));
  }, [harvestsData]);

  // By Section: Sum of harvest_amt by section, grouped by year
  const sectionTrend = useMemo(() => {
    const bySection = {};
    harvestsData.forEach(harvest => {
      const crop = cropsData.find(c => c.id === harvest.crop_id);
      if (!crop) return;
      const zone = zones?.find(z => z.id === crop.zone_id && !z.deleted);
      if (!zone) return;
      const year = new Date(harvest.date).getFullYear();
      if (!bySection[zone.name]) bySection[zone.name] = {};
      if (!bySection[zone.name][year]) bySection[zone.name][year] = 0;
      bySection[zone.name][year] += harvest.harvest_amt || 0;
    });
    const years = Array.from(new Set(Object.values(bySection).flatMap(obj => Object.keys(obj)))).sort();
    return years.map(year => {
      const entry = { year };
      allSections.forEach(section => {
        entry[section] = bySection[section]?.[year] ? Number(bySection[section][year].toFixed(2)) : 0;
      });
      return entry;
    });
  }, [harvestsData, cropsData, zones, allSections]);

  // By Crop Type: Show actual_yield from cropsData directly
  const cropTypeTrend = useMemo(() => {
    // Group by month, then by crop type
    const byMonth = {};
    cropsData.forEach(crop => {
      const cropType = crop.plant_type_cl || crop.plant_type || 'Unknown';
      if (!selectedCropTypes.includes(cropType)) return;
      
      const actualYield = crop.actual_yield || 0;
      if (actualYield === 0) return;
      
      const month = getMonthLabel(crop.transplant_date || crop.planting_date);
      if (!byMonth[month]) byMonth[month] = {};
      if (!byMonth[month][cropType]) byMonth[month][cropType] = 0;
      byMonth[month][cropType] += actualYield;
    });
    const months = Object.keys(byMonth).sort();
    return months.map(month => {
      const entry = { month };
      selectedCropTypes.forEach(type => {
        entry[type] = byMonth[month]?.[type] ? Number(byMonth[month][type].toFixed(2)) : 0;
      });
      return entry;
    });
  }, [cropsData, selectedCropTypes]);

  // --- Crop Type View State and Data ---
  const [selectedPlantTypes, setSelectedPlantTypes] = useState([]);
  const [availablePlantTypes, setAvailablePlantTypes] = useState([]);

  useEffect(() => {
    // Only include plant types where at least one crop has actual yield > 0
    const plantTypeYieldMap = {};
    cropsData.forEach(crop => {
      const actualYield = crop.actual_yield || 0;
      if (crop.plant_type && actualYield > 0) {
        plantTypeYieldMap[crop.plant_type] = true;
      }
    });
    const plantTypes = Object.keys(plantTypeYieldMap).sort((a, b) => a.localeCompare(b));
    setAvailablePlantTypes(plantTypes);
    // If nothing selected, select the first item by default
    if (plantTypes.length > 0 && selectedPlantTypes.length === 0) {
      setSelectedPlantTypes([plantTypes[0]]);
    }
  }, [cropsData]);

  const filteredData = useMemo(() => {
    // Group by date, sum actual yield for all selected types, and collect variety names
    const byDate = {};
    cropsData.forEach(crop => {
      const plantType = crop.plant_type;
      if (!selectedPlantTypes.includes(plantType)) return;
      
      const actualYield = crop.actual_yield || 0;
      if (!crop.transplant_date && !crop.planting_date) return;
      if (actualYield <= 0) return;
      
      const date = crop.transplant_date || crop.planting_date;
      if (!byDate[date]) byDate[date] = { actual_yield: 0, varieties: new Set() };
      byDate[date].actual_yield += actualYield;
      if (crop.variety_name) byDate[date].varieties.add(crop.variety_name);
    });
    const dates = Object.keys(byDate).sort((a, b) => new Date(a) - new Date(b));
    return dates.map(date => ({
      date,
      actual_yield: Number(byDate[date].actual_yield.toFixed(2)),
      varieties: Array.from(byDate[date].varieties).join(', ')
    })).filter(entry => entry.actual_yield > 0);
  }, [cropsData, selectedPlantTypes]);

  const tooltipFormatter = (value, name, props) => {
    const varietyNames = props.payload?.varieties || 'Unknown Variety';
    return [`${value} (Actual Yield)`, varietyNames];
  };

  // Chart lines config
  const getLines = (keys) =>
    keys.map((key, idx) => (
      <Line
        key={key}
        type="monotone"
        dataKey={key}
        stroke={COLORS[idx % COLORS.length]}
        strokeWidth={2}
        dot={false}
        name={key}
        isAnimationActive={false}
      />
    ));

  // Chart data and lines based on view
  let chartData = [];
  let lines = [];
  let legend = true;
  let yAxisLabel = 'Yield (kg/m²)';
  if (view === 'total') {
    chartData = totalTrend;
    lines = [<Line key="yield" type="monotone" dataKey="yield" stroke={COLORS[0]} strokeWidth={3} dot={false} name="Total Yield" isAnimationActive={false} />];
    legend = false;
    yAxisLabel = 'kg';
  } else if (view === 'section') {
    chartData = sectionTrend;
    lines = allSections.map((section, idx) => (
      <Bar key={section} dataKey={section} fill={COLORS[idx % COLORS.length]} name={section} />
    ));
    legend = true;
    yAxisLabel = 'kg';
  } else if (view === 'cropType') {
    chartData = filteredData;
    lines = [
      <Line
        key="actual_yield"
        type="monotone"
        dataKey="actual_yield"
        stroke="#F9AA33"
        strokeWidth={3}
        fillOpacity={1}
        fill="url(#yieldGradient)"
        activeDot={{ r: 8 }}
        dot={{ r: 4 }}
        name={selectedPlantTypes.length > 1 ? 'Combined Actual Yield' : selectedPlantTypes[0]}
      />
    ];
    legend = false;
  }

  return (
    <Card
      sx={{
        backgroundColor: '#181F2A',
        color: '#fff',
        borderRadius: 1.5,
        p: { xs: 2, md: 3 },
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)',
        mb: { xs: 2, md: 2 },
        minHeight: { xs: 350, md: 400 },
        overflow: 'visible'
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 500, fontSize: 15, mr: 2, whiteSpace: 'nowrap' }}>
          Yield Trend
        </Typography>
        <Box display="flex" alignItems="center" gap={1} sx={{ 
          overflow: 'auto',
          '&::-webkit-scrollbar': { 
            height: 4,
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: 2
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: 2,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.5)'
            }
          },
          flexShrink: 0,
          maxWidth: { xs: '60%', md: '50%' }
        }}>
          <Box display="flex" gap={1} sx={{ 
            minWidth: { xs: 'max-content', md: 'max-content' },
            flexShrink: 0,
            width: { xs: 'max-content', md: 'max-content' }
          }}>
            <Button
              variant={view === 'total' ? 'contained' : 'outlined'}
              onClick={() => setView('total')}
              size="small"
              sx={{ 
                color: '#fff', 
                bgcolor: view === 'total' ? '#F9AA33' : '#222C2F',
                fontWeight: 400,
                fontSize: 12,
                height: 26,
                px: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)',
                textTransform: 'capitalize',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '&:hover': {
                  bgcolor: view === 'total' ? '#F9AA33' : '#222C2F',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Total
            </Button>
            <Button
              variant={view === 'section' ? 'contained' : 'outlined'}
              onClick={() => setView('section')}
              size="small"
              sx={{ 
                color: '#fff', 
                bgcolor: view === 'section' ? '#F9AA33' : '#222C2F',
                fontWeight: 400,
                fontSize: 12,
                height: 26,
                px: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)',
                textTransform: 'capitalize',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '&:hover': {
                  bgcolor: view === 'section' ? '#F9AA33' : '#222C2F',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Section
            </Button>
            <Button
              variant={view === 'cropType' ? 'contained' : 'outlined'}
              onClick={() => setView('cropType')}
              size="small"
              sx={{ 
                color: '#fff', 
                bgcolor: view === 'cropType' ? '#F9AA33' : '#222C2F',
                fontWeight: 400,
                fontSize: 12,
                height: 26,
                px: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)',
                textTransform: 'capitalize',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '&:hover': {
                  bgcolor: view === 'cropType' ? '#F9AA33' : '#222C2F',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Crop
            </Button>
          </Box>
          {view === 'cropType' && (
            <Badge
              badgeContent={selectedPlantTypes.length > 1 ? selectedPlantTypes.length : null}
              color="primary"
              sx={{ '& .MuiBadge-badge': { right: -10, top: 8, fontSize: 10, height: 18, minWidth: 18, borderRadius: 9, bgcolor: '#F9AA33', color: '#181F2A', fontWeight: 700 } }}
            >
              <Select
                multiple
                value={selectedPlantTypes}
                onChange={e => setSelectedPlantTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                input={<OutlinedInput />}
                renderValue={selected => {
                  const display = selected.join(', ');
                  return (
                    <Box sx={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>{display}</Box>
                  );
                }}
                size="small"
                sx={{
                  color: '#fff',
                  backgroundColor: '#222C2F',
                  borderRadius: 2,
                  minWidth: 60,
                  maxWidth: 110,
                  fontWeight: 400,
                  height: 26,
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  boxShadow: 0,
                  '.MuiSelect-select': { p: '0.5px 0.5px 0.5px 8px', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 },
                  '.MuiSelect-multiple': { display: 'flex', flexWrap: 'wrap', gap: '4px' },
                  '& .Mui-selected': { backgroundColor: '#1976d2 !important', color: '#fff' },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  px: 1,
                  py: 0.5,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#222C2F',
                      color: '#fff',
                    },
                  },
                }}
              >
                {availablePlantTypes.map(plantType => (
                  <MenuItem key={plantType} value={plantType} sx={{ fontSize: 12 }}>
                    <Checkbox checked={selectedPlantTypes.indexOf(plantType) > -1} sx={{ color: '#90caf9', p: 0.5 }} />
                    <ListItemText primary={plantType} />
                  </MenuItem>
                ))}
              </Select>
            </Badge>
          )}
        </Box>
      </Box>
      <Typography sx={{ color: '#AAB4C8', fontWeight: 400, fontSize: 12, mb: 1, mt: -1 }}>
        {view === 'total' ? 'Total yield in kg' : 
         view === 'section' ? 'Yield by section in kg' : 
         'Yield by crop type in kg/m²'}
      </Typography>
      <Box sx={{ width: '100%', flex: 1, minHeight: 324, mt: 0, height: { xs: 260, md: 320 }, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', p: 0, overflow: 'visible' }}>
        <ResponsiveContainer width="100%" height={280}>
          {view === 'section' ? (
            chartData && chartData.length > 0 ? (
              <BarChart data={chartData} margin={{ top: 8, right: 20, left: -10, bottom: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#283144" />
                <XAxis dataKey="year" tick={{ fill: '#AAB4C8', fontSize: 10 }} />
                <YAxis tick={{ fill: '#AAB4C8', fontSize: 10 }} />
                <Tooltip 
                  formatter={(value, name) => [`${value} kg`, name]}
                  contentStyle={{ 
                    backgroundColor: '#222C2F', 
                    color: '#fff', 
                    border: '1px solid #283144'
                  }}
                />
                <Legend wrapperStyle={{ color: '#AAB4C8', fontSize: 11 }} />
                {lines}
              </BarChart>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                <Typography sx={{ color: '#AAB4C8', textAlign: 'center', fontSize: 16 }}>No data available for this view.</Typography>
              </Box>
            )
          ) : view === 'cropType' ? (
            filteredData && filteredData.length > 0 ? (
              <AreaChart
                data={filteredData}
                margin={{ top: 8, right: 20, left: -10, bottom: 16 }}
              >
                <defs>
                  <linearGradient id="yieldGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F9AA33" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#F9AA33" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#ccc', fontSize: 9 }}
                  angle={-45}
                  textAnchor="end"
                  label={{
                    value: 'Transplant/Planting Date',
                    position: 'insideBottom',
                    offset: -30,
                    fill: '#ccc',
                    fontSize: 10,
                  }}
                />
                <YAxis
                  tick={{ fill: '#ccc', fontSize: 9 }}
                />
                <Tooltip
                  formatter={tooltipFormatter}
                  contentStyle={{ 
                    backgroundColor: '#333', 
                    color: '#fff'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="actual_yield"
                  stroke="#F9AA33"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#yieldGradient)"
                  activeDot={{ r: 8 }}
                  dot={{ r: 4 }}
                  name={selectedPlantTypes.length > 1 ? 'Combined Actual Yield' : selectedPlantTypes[0]}
                />
              </AreaChart>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                <Typography sx={{ color: '#AAB4C8', textAlign: 'center', fontSize: 16 }}>No data available for selected crop types.</Typography>
              </Box>
            )
          ) : (
            chartData && chartData.length > 0 ? (
              <AreaChart data={chartData} margin={{ top: 8, right: 20, left: -8, bottom: 10 }}>
                <defs>
                  <linearGradient id="totalYieldGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F9AA33" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#F9AA33" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#AAB4C8', fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  interval={Math.ceil((chartData.length - 1) / 5)}
                />
                <YAxis tick={{ fill: '#AAB4C8', fontSize: 10 }} />
                <Tooltip 
                  formatter={v => `${v} kg`} 
                  contentStyle={{ 
                    backgroundColor: '#222C2F', 
                    color: '#fff', 
                    border: '1px solid #283144'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="yield"
                  stroke="#F9AA33"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#totalYieldGradient)"
                  activeDot={{ r: 8 }}
                  dot={{ r: 4 }}
                  name="Total Yield"
                />
              </AreaChart>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                <Typography sx={{ color: '#AAB4C8', textAlign: 'center', fontSize: 16 }}>No harvest data available.</Typography>
              </Box>
            )
          )}
        </ResponsiveContainer>
      </Box>
    </Card>
  );
};

export default YieldTrendChart;
